import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, ChevronRight, FileText, User, Mail, Phone, Calendar, Filter, SortAsc, Eye, Trash2, X } from 'lucide-react';
import { supabase } from "../utils/superbase";
import './JobApplicantDashboard.css';

const JobApplicantDashboard = () => {
  const [applicants, setApplicants] = useState([]);
  const [filteredApplicants, setFilteredApplicants] = useState([]);
  const [expandedApplicant, setExpandedApplicant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [deleteModal, setDeleteModal] = useState({ show: false, applicantId: null, applicantName: '', deleting: false });
  const [statusNotification, setStatusNotification] = useState({ show: false, message: '' });
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('dateApplied');
  const [sortOrder, setSortOrder] = useState('desc');
  const modalRef = useRef(null);
  const firstFocusableElementRef = useRef(null);
  const lastFocusableElementRef = useRef(null);

  // Parse candidate data from JSON string
  const parseApplicantData = (candidateDataArray) => {
    try {
      if (candidateDataArray && candidateDataArray.length > 0) {
        return JSON.parse(candidateDataArray[0]);
      }
      return {};
    } catch (error) {
      console.error('Error parsing candidate data:', error);
      return {};
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    } catch {
      return dateString;
    }
  };

  // Format timestamp for status changes
  const formatTimestamp = (date) => {
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
      timeZone: 'Africa/Lagos'
    });
  };

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Focus trap for modal
  useEffect(() => {
    if (deleteModal.show) {
      const handleKeyDown = (e) => {
        if (e.key === 'Tab') {
          if (e.shiftKey && document.activeElement === firstFocusableElementRef.current) {
            e.preventDefault();
            lastFocusableElementRef.current.focus();
          } else if (!e.shiftKey && document.activeElement === lastFocusableElementRef.current) {
            e.preventDefault();
            firstFocusableElementRef.current.focus();
          }
        }
        if (e.key === 'Escape') {
          closeDeleteModal();
        }
      };

      firstFocusableElementRef.current?.focus();
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [deleteModal.show]);

  // Fetch data from Supabase
  async function getdata() {
    try {
      const { data, error } = await supabase.from("Received Applications").select();
      if (error) {
        console.error('Error fetching data:', error);
        setStatusNotification({ show: true, message: 'Failed to load applicants. Please try again.' });
        setTimeout(() => setStatusNotification({ show: false, message: '' }), 3000);
        return [];
      }
      return data || [];
    } catch (error) {
      console.error('Error in getdata:', error);
      setStatusNotification({ show: true, message: 'Network error. Please check your connection.' });
      setTimeout(() => setStatusNotification({ show: false, message: '' }), 3000);
      return [];
    }
  }

  // Status levels configuration
  const statusLevels = [
    { value: null, label: 'Fresh Applicant', class: 'status-fresh' },
    { value: 'prospect', label: 'Prospect', class: 'status-prospect' },
    { value: 'interviewed', label: 'Interviewed', class: 'status-interviewed' },
    { value: 'accepted', label: 'Accepted', class: 'status-accepted' }
  ];

  // Update applicant status
  const updateApplicantStatus = async (applicantId, newStatus) => {
    try {
      const timestamp = formatTimestamp(new Date());
      const { error } = await supabase
        .from('Received Applications')
        .update({ 
          current_level: newStatus,
          last_status_change: new Date().toISOString()
        })
        .eq('id', applicantId);

      if (error) {
        console.error('Error updating status:', error);
        setStatusNotification({ show: true, message: 'Failed to update status. Please try again.' });
        setTimeout(() => setStatusNotification({ show: false, message: '' }), 3000);
        return;
      }

      setApplicants(prevApplicants =>
        prevApplicants.map(applicant =>
          applicant.id === applicantId
            ? { ...applicant, current_level: newStatus, last_status_change: timestamp }
            : applicant
        )
      );

      setStatusNotification({
        show: true,
        message: `Status updated to ${getStatusInfo(newStatus).label} at ${timestamp}`
      });
      setTimeout(() => setStatusNotification({ show: false, message: '' }), 5000);
    } catch (error) {
      console.error('Error updating status:', error);
      setStatusNotification({ show: true, message: 'Network error. Please check your connection.' });
      setTimeout(() => setStatusNotification({ show: false, message: '' }), 3000);
    }
  };

  // Delete applicant
  const deleteApplicant = async (applicantId) => {
    setDeleteModal(prev => ({ ...prev, deleting: true }));
    try {
      const { error } = await supabase
        .from('Received Applications')
        .delete()
        .eq('id', applicantId);

      if (error) {
        console.error('Error deleting applicant:', error);
        setStatusNotification({ show: true, message: 'Failed to delete applicant. Please try again.' });
        setTimeout(() => setStatusNotification({ show: false, message: '' }), 3000);
        setDeleteModal(prev => ({ ...prev, deleting: false }));
        return;
      }

      setApplicants(prevApplicants =>
        prevApplicants.filter(applicant => applicant.id !== applicantId)
      );
      setDeleteModal({ show: false, applicantId: null, applicantName: '', deleting: false });
      setStatusNotification({ show: true, message: 'Applicant deleted successfully.' });
      setTimeout(() => setStatusNotification({ show: false, message: '' }), 3000);
    } catch (error) {
      console.error('Error deleting applicant:', error);
      setStatusNotification({ show: true, message: 'Network error. Please check your connection.' });
      setTimeout(() => setStatusNotification({ show: false, message: '' }), 3000);
      setDeleteModal(prev => ({ ...prev, deleting: false }));
    }
  };

  // Get status info by value
  const getStatusInfo = (statusValue) => {
    return statusLevels.find(level => level.value === statusValue) || statusLevels[0];
  };

  // Filter and sort applicants
  const filterAndSortApplicants = (applicantsList) => {
    let filtered = [...applicantsList];

    if (statusFilter !== 'all') {
      filtered = filtered.filter(applicant => {
        if (statusFilter === 'fresh') {
          return !applicant.current_level;
        }
        return applicant.current_level === statusFilter;
      });
    }

    filtered.sort((a, b) => {
      let aValue, bValue;

      switch (sortBy) {
        case 'dateApplied':
          aValue = new Date(a['time applied']);
          bValue = new Date(b['time applied']);
          break;
        case 'status':
          aValue = getStatusInfo(a.current_level).label;
          bValue = getStatusInfo(b.current_level).label;
          break;
        case 'position':
          aValue = a.parsedData?.jobPosition || '';
          bValue = b.parsedData?.jobPosition || '';
          break;
        case 'name':
          aValue = a.candidate_name;
          bValue = b.candidate_name;
          break;
        default:
          return 0;
      }

      if (sortBy === 'dateApplied') {
        return sortOrder === 'desc' ? bValue - aValue : aValue - bValue;
      } else {
        const comparison = aValue.localeCompare(bValue);
        return sortOrder === 'desc' ? -comparison : comparison;
      }
    });

    return filtered;
  };

  useEffect(() => {
    const filtered = filterAndSortApplicants(applicants);
    setFilteredApplicants(filtered);
  }, [applicants, statusFilter, sortBy, sortOrder]);

  useEffect(() => {
    const loadData = async () => {
      try {
        const dbdata = await getdata();
        console.log('Fetched data:', dbdata);
        
        const processedData = dbdata.map(applicant => ({
          ...applicant,
          parsedData: parseApplicantData(applicant.candidate_data),
          last_status_change: applicant.last_status_change ? formatTimestamp(new Date(applicant.last_status_change)) : null
        }));
        
        setApplicants(processedData);
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const toggleExpand = (applicantId) => {
    setExpandedApplicant(expandedApplicant === applicantId ? null : applicantId);
  };

  const openCV = (cvUrl) => {
    window.open(cvUrl, '_blank');
  };

  const showDeleteModal = (applicantId, applicantName) => {
    setDeleteModal({ show: true, applicantId, applicantName, deleting: false });
  };

  const closeDeleteModal = () => {
    setDeleteModal({ show: false, applicantId: null, applicantName: '', deleting: false });
  };

  const closeNotification = () => {
    setStatusNotification({ show: false, message: '' });
  };

  if (loading) {
    return (
      <div className="loading-container">
        <h2>Loading applicants...</h2>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <h1 className="dashboard-title">Job Applicant Dashboard</h1>

      {/* Status Change Notification */}
      {statusNotification.show && (
        <div className="status-notification" role="alert">
          {statusNotification.message}
          <button
            className="notification-close"
            onClick={closeNotification}
            aria-label="Close notification"
          >
            <X size={16} />
          </button>
        </div>
      )}

      {/* Filter and Sort Controls */}
      <div className="filter-container">
        <div className="filter-group">
          <label className="filter-label" htmlFor="status-filter">
            <Filter size={16} />
            Filter by Status:
          </label>
          <select
            id="status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-select"
            aria-label="Filter by status"
          >
            <option value="all">All Applicants ({applicants.length})</option>
            <option value="fresh">Fresh Applicants ({applicants.filter(a => !a.current_level).length})</option>
            <option value="prospect">Prospects ({applicants.filter(a => a.current_level === 'prospect').length})</option>
            <option value="interviewed">Interviewed ({applicants.filter(a => a.current_level === 'interviewed').length})</option>
            <option value="accepted">Accepted ({applicants.filter(a => a.current_level === 'accepted').length})</option>
          </select>
        </div>

        <div className="filter-group">
          <label className="filter-label" htmlFor="sort-by">
            <SortAsc size={16} />
            Sort by:
          </label>
          <select
            id="sort-by"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="filter-select"
            aria-label="Sort by"
          >
            <option value="dateApplied">Date Applied</option>
            <option value="status">Status</option>
            <option value="position">Position</option>
            <option value="name">Name</option>
          </select>
        </div>

        <div className="filter-group">
          <label className="filter-label" htmlFor="sort-order">Order:</label>
          <select
            id="sort-order"
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
            className="filter-select"
            aria-label="Sort order"
          >
            <option value="desc">
              {sortBy === 'dateApplied' ? 'Newest First' : 'Z to A'}
            </option>
            <option value="asc">
              {sortBy === 'dateApplied' ? 'Oldest First' : 'A to Z'}
            </option>
          </select>
        </div>

        <div className="results-count" aria-live="polite">
          Showing {filteredApplicants.length} of {applicants.length} applicants
        </div>
      </div>
      
      {filteredApplicants.length === 0 ? (
        <div className="no-results">
          <h3>
            {applicants.length === 0 
              ? 'No applicants found' 
              : `No applicants match the selected filter (${getStatusInfo(statusFilter === 'fresh' ? null : statusFilter).label})`
            }
          </h3>
          {applicants.length > 0 && (
            <button
              onClick={() => setStatusFilter('all')}
              className="show-all-button"
            >
              Show All Applicants
            </button>
          )}
        </div>
      ) : (
        <div>
          {filteredApplicants.map((applicant) => {
            const isExpanded = expandedApplicant === applicant.id;
            const data = applicant.parsedData;
            const statusClass = getStatusInfo(applicant.current_level).class;
            const applicationDate = formatDate(applicant['time applied']);
            
            return (
              <div key={applicant.id} className="applicant-card">
                <div
                  onClick={() => toggleExpand(applicant.id)}
                  className={`summary-row ${isExpanded ? 'expanded' : ''}`}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && toggleExpand(applicant.id)}
                  aria-expanded={isExpanded}
                  aria-label={`Toggle details for ${applicant.candidate_name}`}
                >
                  <div className="chevron-container">
                    {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                  </div>
                  
                  <div className="summary-content">
                    <div className="summary-item name">
                      <User size={16} />
                      <span>{applicant.candidate_name}</span>
                    </div>
                    <div className="summary-item email">
                      <Mail size={16} />
                      <span>{applicant.candidate_email}</span>
                    </div>
                    <div className="summary-item phone">
                      <Phone size={16} />
                      <span>{applicant.candidate_phone}</span>
                    </div>
                    <div className="summary-item status">
                      <span className={`status-badge ${statusClass}`}>
                        {getStatusInfo(applicant.current_level).label}
                      </span>
                      <span className="status-date">
                        <Calendar size={14} />
                        {applicationDate}
                      </span>
                    </div>
                  </div>
                </div>

                {isExpanded && (
                  <div className="expanded-details">
                    <div className="action-container">
                      <div className="status-group">
                        <label className="status-label" htmlFor={`status-${applicant.id}`}>
                          Application Status:
                        </label>
                        <select
                          id={`status-${applicant.id}`}
                          value={applicant.current_level || ''}
                          onChange={(e) => updateApplicantStatus(applicant.id, e.target.value || null)}
                          className="status-select"
                          aria-label={`Change status for ${applicant.candidate_name}`}
                        >
                          {statusLevels.map((level) => (
                            <option key={level.value || 'null'} value={level.value || ''}>
                              {level.label}
                            </option>
                          ))}
                        </select>
                        {applicant.last_status_change && (
                          <p className="status-timestamp">
                            Last updated: {applicant.last_status_change}
                          </p>
                        )}
                      </div>

                      <div className="action-buttons">
                        <button
                          onClick={() => openCV(applicant.cv)}
                          className="cv-button"
                          aria-label={`View CV for ${applicant.candidate_name}`}
                        >
                          <FileText size={16} />
                          View CV
                        </button>

                        {applicant.idphoto && (
                          <button
                            onClick={() => window.open(applicant.idphoto, '_blank')}
                            className="id-photo-button"
                            aria-label={`View ID photo for ${applicant.candidate_name}`}
                          >
                            <Eye size={16} />
                            View ID Photo
                          </button>
                        )}

                        <button
                          onClick={() => showDeleteModal(applicant.id, applicant.candidate_name)}
                          className="delete-button"
                          aria-label={`Delete application for ${applicant.candidate_name}`}
                        >
                          <Trash2 size={16} />
                          Delete
                        </button>
                      </div>
                    </div>

                    <div className="details-container">
                      <div className="details-grid">
                        <div className="details-section">
                          <h3 className="section-title personal">Personal Information</h3>
                          <div className="section-content">
                            <p><strong>Full Name:</strong> {data.title} {data.firstName} {data.lastName}</p>
                            <p><strong>Date of Birth:</strong> {formatDate(data.DOB)}</p>
                            <p><strong>Gender:</strong> {data.gender}</p>
                            <p><strong>Nationality:</strong> {data.nationality}</p>
                            <p className="break-all"><strong>Phone:</strong> {data.phone}</p>
                            <p className="break-all"><strong>Mobile:</strong> {data.mobileNo}</p>
                            <p className="break-all"><strong>Email:</strong> {data.email}</p>
                            <p><strong>Address:</strong> {data.address}, {data.city}, {data.county}, {data.zipCode}</p>
                            <p><strong>Country:</strong> {data.country}</p>
                            <p className="break-all"><strong>National Insurance No:</strong> {data.nationalInsuranceNo}</p>
                          </div>
                        </div>

                        {data.employers && data.employers.length > 0 && (
                          <div className="details-section">
                            <h3 className="section-title employment">Employment History</h3>
                            {data.employers.map((emp, index) => (
                              <div key={index} className="section-content">
                                <p><strong>Employer:</strong> {emp.employer}</p>
                                <p><strong>Position:</strong> {emp.position}</p>
                                <p><strong>Period:</strong> {formatDate(emp.dateFrom)} - {formatDate(emp.dateTo)}</p>
                                <p><strong>Duties:</strong> {emp.duties}</p>
                                <p><strong>Salary:</strong> {emp.salary}</p>
                                <p><strong>Reason for Leaving:</strong> {emp.reasonForLeaving}</p>
                                {index < data.employers.length - 1 && <hr className="section-divider" />}
                              </div>
                            ))}
                          </div>
                        )}

                        {data.education && data.education.length > 0 && (
                          <div className="details-section">
                            <h3 className="section-title education">Education</h3>
                            {data.education.map((edu, index) => (
                              <div key={index} className="section-content">
                                <p><strong>Institution:</strong> {edu.institution}</p>
                                <p><strong>Course:</strong> {edu.course}</p>
                                <p><strong>Year:</strong> {edu.year}</p>
                                {edu.grade && <p><strong>Grade:</strong> {edu.grade}</p>}
                              </div>
                            ))}
                          </div>
                        )}

                        <div className="details-section">
                          <h3 className="section-title next-of-kin">Next of Kin Contact</h3>
                          <div className="section-content">
                            <p><strong>Name:</strong> {data.kinTitle} {data.kinFirstName} {data.kinLastName}</p>
                            <p><strong>Relationship:</strong> {data.kinRelationship}</p>
                            <p className="break-all"><strong>Phone:</strong> {data.kinPhone}</p>
                            <p className="break-all"><strong>Mobile:</strong> {data.kinMobileNo}</p>
                            <p className="break-all"><strong>Email:</strong> {data.kinEmail}</p>
                            <p><strong>Address:</strong> {data.kinAddress}, {data.kinCity}, {data.kinZipCode}</p>
                            <p><strong>Country:</strong> {data.KinCountry}</p>
                          </div>
                        </div>

                        <div className="details-section">
                          <h3 className="section-title availability">Availability & Preferences</h3>
                          <div className="section-content">
                            <p><strong>Date Available:</strong> {formatDate(data.dateAvailable)}</p>
                            <p><strong>How heard about job:</strong> {data.hearAboutJob}</p>
                            <p><strong>Visa Type:</strong> {data.visaType}</p>
                            <p><strong>Driver's License:</strong> {data.DriverLicenseHolder}</p>
                            <p><strong>Commute Method:</strong> {data.commuteMethod}</p>
                            <p><strong>English Spoken:</strong> {data.englishSpoken}</p>
                            <p><strong>English Written:</strong> {data.englishWritten}</p>
                            <p><strong>Other Languages:</strong> {data.otherLanguages}</p>
                          </div>
                        </div>

                        <div className="details-section">
                          <h3 className="section-title health">Health Information</h3>
                          <div className="section-content">
                            <p><strong>Long Term Illness:</strong> {data.longTermIllness}</p>
                            <p><strong>Sick Leave/Injury:</strong> {data.sickLeaveInjury}</p>
                            <p><strong>Back/Neck Injury:</strong> {data.backNeckInjury}</p>
                            <p><strong>Registered Disabled:</strong> {data.registeredDisabled}</p>
                            <p><strong>Days Absent (last year):</strong> {data.daysAbsent}</p>
                            <p><strong>Absence Reason:</strong> {data.absenceReason}</p>
                            <p><strong>Health Details:</strong> {data.healthDetails}</p>
                            <p><strong>GP Name:</strong> {data.gpName}</p>
                            <p><strong>GP Address:</strong> {data.gpAddress}, {data.gpCity}, {data.gpZipCode}, {data.gpCountry}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModal.show && (
        <div className="modal-overlay" role="dialog" aria-labelledby="modal-title">
          <div className="modal-content" ref={modalRef}>
            <h3 id="modal-title" className="modal-title">Confirm Deletion</h3>
            <p>Are you sure you want to delete the application for <strong>{deleteModal.applicantName}</strong>? This action cannot be undone.</p>
            <div className="modal-buttons">
              <button
                ref={firstFocusableElementRef}
                onClick={closeDeleteModal}
                className="modal-cancel-button"
                disabled={deleteModal.deleting}
                aria-label="Cancel deletion"
              >
                Cancel
              </button>
              <button
                ref={lastFocusableElementRef}
                onClick={() => deleteApplicant(deleteModal.applicantId)}
                className="modal-confirm-button"
                disabled={deleteModal.deleting}
                aria-label="Confirm deletion"
              >
                {deleteModal.deleting ? 'Deleting...' : 'Confirm Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobApplicantDashboard;